import { QRCodeSVG } from "qrcode.react";
import { useEffect } from "react";
import { useI18n } from "@/context/I18nContext";

/**
 * SaleReceipt — NYLSU-branded printable summary.
 * Uses a print stylesheet (inline) so window.print() hides everything else.
 * Internal document — legal e-invoice remains Paraşüt's job.
 */
export default function SaleReceipt({ sale, customer, location }) {
  const { fmtCurrency, fmtDateTime } = useI18n();
  useEffect(() => {
    // Trigger print exactly once when component mounts if flagged
    if (window.location.hash === "#print") {
      setTimeout(() => window.print(), 300);
    }
  }, []);
  if (!sale) return null;
  const url = `${window.location.origin}/sales/${sale.id}`;
  const invStatus = (sale.invoice_status || "pending").toUpperCase();
  const invTone = invStatus === "SYNCED" ? "#5C7A55" : invStatus === "FAILED" ? "#8C3B3B" : "#9C6B2A";

  return (
    <>
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print, [data-testid="sidebar"], [data-testid="topbar"], header, aside { display: none !important; }
          .receipt-page { padding: 0 !important; margin: 0 !important; }
          .receipt-sheet { box-shadow: none !important; border: none !important; margin: 0 !important; }
          @page { size: A4; margin: 18mm 16mm; }
        }
        .receipt-sheet {
          background: #FBFAF3;
          color: #1A1A1A;
          font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif;
        }
        .receipt-serif { font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif; }
      `}</style>
      <div className="receipt-page py-8 flex justify-center" data-testid="sale-receipt">
        <div className="receipt-sheet w-full max-w-[820px] p-12 border" style={{ borderColor: "#E5E1D2" }}>
          {/* Letterhead */}
          <div className="flex items-start justify-between mb-10 border-b pb-6" style={{ borderColor: "#1A1A1A" }}>
            <div>
              <div className="receipt-serif text-4xl tracking-[0.3em] leading-none">NYLSU</div>
              <div className="text-[10px] uppercase tracking-[0.25em] mt-2 text-[#8F8D84]">Atelier · Istanbul · Paris</div>
              <div className="text-xs mt-4 leading-relaxed text-[#5C5A54]">
                Teşvikiye Cad. No 45, Nişantaşı 34365<br />
                İstanbul · Türkiye<br />
                +90 212 555 0100 · atelier@nylsu.com
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8F8D84]">Sale Summary</div>
              <div className="receipt-serif text-3xl mt-1">{sale.sale_number}</div>
              <div className="text-xs text-[#5C5A54] mt-2">{fmtDateTime(sale.completed_at || sale.created_at)}</div>
              {location && <div className="text-xs text-[#5C5A54] mt-1">{location.name}</div>}
              <div className="mt-3 inline-block text-[9px] uppercase tracking-[0.2em] px-2 py-1 border"
                style={{ borderColor: invTone, color: invTone }}>
                Paraşüt · {invStatus}
              </div>
            </div>
          </div>

          {/* Bill-to */}
          <div className="grid grid-cols-2 gap-10 mb-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] mb-2">Client</div>
              <div className="receipt-serif text-xl">{customer?.name || (sale.walk_in ? "Walk-in guest" : "—")}</div>
              {customer?.email && <div className="text-xs text-[#5C5A54] mt-1">{customer.email}</div>}
              {customer?.phone && <div className="text-xs text-[#5C5A54]">{customer.phone}</div>}
              {customer?.address && <div className="text-xs text-[#5C5A54] mt-1 whitespace-pre-line">{customer.address}</div>}
              {customer?.tax_id && <div className="text-xs text-[#5C5A54] mt-1">Tax ID · {customer.tax_id}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] mb-2">Channel</div>
              <div className="text-sm capitalize">{sale.channel}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] mt-4 mb-2">Prepared by</div>
              <div className="text-sm">{sale.salesperson_email || "—"}</div>
            </div>
          </div>

          {/* Items */}
          <table className="w-full text-sm mb-6">
            <thead>
              <tr className="border-b border-t" style={{ borderColor: "#1A1A1A" }}>
                <th className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] py-3 text-left">Item</th>
                <th className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] py-3 text-right w-14">Qty</th>
                <th className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] py-3 text-right w-24">Unit</th>
                <th className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] py-3 text-right w-20">Disc.</th>
                <th className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] py-3 text-right w-28">Total</th>
              </tr>
            </thead>
            <tbody>
              {(sale.items || []).map((it) => (
                <tr key={it.id} className="border-b" style={{ borderColor: "#E5E1D2" }}>
                  <td className="py-3">
                    <div className="receipt-serif text-base">{it.style_name}</div>
                    <div className="text-xs text-[#5C5A54]">{it.color} · {it.size}</div>
                    <div className="font-mono text-[10px] text-[#8F8D84]">{it.sku}</div>
                  </td>
                  <td className="py-3 text-right font-mono">{it.quantity}</td>
                  <td className="py-3 text-right font-mono">{fmtCurrency(it.unit_price)}</td>
                  <td className="py-3 text-right font-mono">−{fmtCurrency(it.line_discount_amount_effective || 0)}</td>
                  <td className="py-3 text-right font-mono">{fmtCurrency(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-10">
            <div className="w-80 space-y-2 text-sm">
              <div className="flex justify-between text-[#5C5A54]"><span>Subtotal</span><span className="font-mono">{fmtCurrency(sale.subtotal)}</span></div>
              <div className="flex justify-between text-[#5C5A54]"><span>Item discounts</span><span className="font-mono">−{fmtCurrency(sale.item_discount_total || 0)}</span></div>
              {sale.sale_discount_amount_effective > 0 &&
                <div className="flex justify-between text-[#5C5A54]"><span>Sale discount</span><span className="font-mono">−{fmtCurrency(sale.sale_discount_amount_effective)}</span></div>}
              <div className="flex justify-between border-t pt-2" style={{ borderColor: "#E5E1D2" }}>
                <span>Net</span><span className="font-mono">{fmtCurrency(sale.net_sales)}</span>
              </div>
              {sale.tax_total > 0 &&
                <div className="flex justify-between text-[#5C5A54]"><span>Tax</span><span className="font-mono">{fmtCurrency(sale.tax_total)}</span></div>}
              <div className="flex justify-between border-t border-b py-2 receipt-serif text-lg" style={{ borderColor: "#1A1A1A" }}>
                <span>Total</span><span className="font-mono">{fmtCurrency(sale.grand_total, sale.currency)}</span>
              </div>
            </div>
          </div>

          {/* Payments */}
          {sale.payments?.length > 0 && (
            <div className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84] mb-2">Payment</div>
              <table className="w-full text-sm">
                <tbody>
                  {sale.payments.map((p) => (
                    <tr key={p.id} className="border-b" style={{ borderColor: "#E5E1D2" }}>
                      <td className="py-2 capitalize">{p.method.replace(/_/g, " ")}</td>
                      <td className="py-2 text-xs text-[#5C5A54]">{p.reference || ""}</td>
                      <td className="py-2 text-right font-mono">{fmtCurrency(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer + QR */}
          <div className="mt-14 pt-6 border-t flex items-end justify-between" style={{ borderColor: "#E5E1D2" }}>
            <div className="text-[10px] leading-relaxed text-[#8F8D84] max-w-md">
              This is an internal sale summary generated by NYLSU ERP.
              The legal tax invoice is issued through Paraşüt and available to the client on request.
              <div className="mt-2 receipt-serif text-sm text-[#1A1A1A]">Merci — thank you for wearing NYLSU.</div>
            </div>
            <div className="text-center">
              <div className="p-2 border" style={{ background: "white", borderColor: "#E5E1D2" }}>
                <QRCodeSVG value={url} size={96} level="M" bgColor="#FFFFFF" fgColor="#1A1A1A" />
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#8F8D84] mt-2">Open in ERP</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
